// /app/integrations — per-brand social account fan-out.
//
// Ported visually from design-handoff/src/pages/secondary.jsx
// IntegrationsPage: a vertical list of brand cards, each showing a
// 5-column grid (one per platform) with connection status. Click into
// a card or cell → go to the brand cockpit.
//
// Backend wiring kept from the previous version:
//   GET /v1/brands/:id/social-accounts (called per brand via useQueries)
// One HTTP per brand. For ≤50 brands an agency realistically has, this
// is fine; a workspace-wide endpoint would be a backend session.

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useBrands } from "@/hooks/use-persona";
import { PageHead, BrandMark, PlatformChip } from "@/components/primitives";
import { isIdentityGated, identityGateBody, IdentityGateConfirm } from "@/components/IdentityGate";
import type { Platform, SocialAccount } from "@/lib/types";

// Map our user-facing platform id to the backend's OAuth provider
// segment. Meta covers both Facebook + Instagram in one consent;
// LinkedIn + X each have their own. Telegram doesn't OAuth.
const OAUTH_PROVIDER: Partial<Record<Platform, "meta" | "linkedin" | "x">> = {
  facebook: "meta",
  instagram: "meta",
  linkedin: "linkedin",
  x: "x",
};

function startOAuthFor(brandId: string, platform: Platform): Promise<void> {
  const provider = OAUTH_PROVIDER[platform];
  if (!provider) return Promise.reject(new Error("unsupported platform"));
  return api<{ authorize_url: string }>(
    `/brands/${brandId}/social-accounts/${provider}/oauth/start`,
    { method: "POST" },
  ).then((r) => { window.location.href = r.authorize_url; });
}

export const Route = createFileRoute("/app/integrations")({
  component: IntegrationsPage,
});

const ALL_PLATFORMS: Platform[] = [
  "instagram",
  "facebook",
  "x",
  "linkedin",
  "telegram",
];
const PLATFORM_LABEL: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  x: "X",
  linkedin: "LinkedIn",
  telegram: "Telegram",
};

function IntegrationsPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const navigate = useNavigate();

  const { data: brandsData, isLoading: brandsLoading } = useBrands();
  const brands = brandsData?.data ?? [];

  const queries = useQueries({
    queries: brands.map((b) => ({
      queryKey: ["brand", b.id, "social-accounts"],
      queryFn: async () => {
        const r = await api<
          | SocialAccount[]
          | { data?: SocialAccount[]; social_accounts?: SocialAccount[] }
        >(`/brands/${b.id}/social-accounts`);
        // Tolerate any of: raw array, {data: [...]}, {social_accounts: [...]},
        // or a malformed payload — always return an array so the loop below
        // can't blow up with "is not iterable".
        if (Array.isArray(r)) return r;
        if (r && typeof r === "object") {
          const obj = r as { data?: unknown; social_accounts?: unknown };
          if (Array.isArray(obj.data)) return obj.data as SocialAccount[];
          if (Array.isArray(obj.social_accounts)) return obj.social_accounts as SocialAccount[];
        }
        return [] as SocialAccount[];
      },
      staleTime: 60_000,
    })),
  });
  const allLoaded = queries.every((q) => !q.isLoading);

  // Map brand → { platform → account } for quick per-cell lookup.
  const matrix = new Map<string, Partial<Record<Platform, SocialAccount>>>();
  brands.forEach((b, i) => {
    const raw = queries[i]?.data;
    const accs: SocialAccount[] = Array.isArray(raw) ? raw : [];
    const row: Partial<Record<Platform, SocialAccount>> = {};
    for (const a of accs) {
      if (!row[a.platform]) row[a.platform] = a;
    }
    matrix.set(b.id, row);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "08 · Entegrasyonlar" : "08 · Integrations"}
        title={tr ? "Marka başına sosyal hesaplar." : "Social accounts, per brand."}
        subtitle={
          tr
            ? "Meta (FB + IG), X, LinkedIn ve Telegram bağla. Token süresi dolunca yeniden bağla."
            : "Connect Meta (FB + IG), X, LinkedIn and Telegram. Reconnect when tokens expire."
        }
      />

      <div className="scroll-y" style={{ flex: 1, padding: "24px 32px 48px" }}>
        {brandsLoading || !allLoaded ? (
          <SkeletonList />
        ) : brands.length === 0 ? (
          <EmptyState tr={tr} onAdd={() => navigate({ to: "/app/brands/new" })} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {brands.map((b) => {
              const row = matrix.get(b.id) ?? {};
              const connectedCount = ALL_PLATFORMS.filter(
                (p) => row[p]?.status === "active",
              ).length;
              return (
                <div key={b.id} className="card" style={{ padding: 22 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <BrandMark brand={b} size={32} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{b.trade_name}</div>
                      <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                        {connectedCount}/{ALL_PLATFORMS.length}{" "}
                        {tr ? "bağlı" : "connected"}
                      </div>
                    </div>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() =>
                        navigate({
                          to: "/app/brands/$brandId",
                          params: { brandId: b.id },
                        })
                      }
                    >
                      {tr ? "Markaya git →" : "Open brand →"}
                    </button>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(5, 1fr)",
                      gap: 10,
                    }}
                  >
                    {ALL_PLATFORMS.map((pl) => (
                      <PlatformCell
                        key={pl}
                        brandId={b.id}
                        platform={pl}
                        account={row[pl]}
                        tr={tr}
                        onTelegramRoute={() =>
                          navigate({
                            to: "/app/brands/$brandId",
                            params: { brandId: b.id },
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// PlatformCell — one tile in the per-brand 5-col grid. Connect / Reconnect
// fires OAuth directly (POST /v1/brands/:id/social-accounts/<provider>/oauth/start)
// instead of routing the operator into the brand cockpit and making them
// click again. Telegram is the exception — it's a paste-the-channel-handle
// flow that lives in the brand Accounts tab.
function PlatformCell({
  brandId,
  platform,
  account,
  tr,
  onTelegramRoute,
}: {
  brandId: string;
  platform: Platform;
  account: SocialAccount | undefined;
  tr: boolean;
  onTelegramRoute: () => void;
}) {
  const connected = account?.status === "active";
  // Backend uses reauth_required / action_required (not the legacy "expired").
  const needsReconnect =
    account?.status === "expired" ||
    account?.status === "reauth_required" ||
    account?.status === "action_required";
  const isIdentity = account?.status === "action_required";
  const isTelegram = platform === "telegram";
  // FB 368 identity gate — reconnect can't fix it; require an explicit
  // acknowledgment click (IdentityGateConfirm) before the OAuth redirect.
  // Keyed strictly on metadata.disconnect_reason === "identity".
  const identityGated = isIdentityGated(account);
  const [identityConfirm, setIdentityConfirm] = useState(false);

  const oauthMutation = useMutation({
    mutationFn: () => startOAuthFor(brandId, platform),
  });

  const handleConnectClick = () => {
    if (isTelegram) {
      onTelegramRoute();
      return;
    }
    if (identityGated) {
      // Open (or re-focus) the confirm panel — only ITS button may
      // start the OAuth redirect.
      setIdentityConfirm(true);
      return;
    }
    oauthMutation.mutate();
  };
  const errMsg = oauthMutation.error
    ? (oauthMutation.error as Error).message
    : null;

  return (
    <div
      style={{
        padding: 14,
        background: "var(--base)",
        border: `1px solid ${needsReconnect ? "var(--error)" : "var(--b2)"}`,
        borderRadius: 8,
      }}
    >
      <PlatformChip platform={platform} size={28} active={connected} />
      <div style={{ fontSize: 12, fontWeight: 500, marginTop: 10 }}>
        {PLATFORM_LABEL[platform]}
      </div>
      {connected && (
        <div className="mono" style={{ fontSize: 9, color: "var(--success)", marginTop: 6 }}>
          ● {tr ? "Bağlı" : "Connected"}
        </div>
      )}
      {needsReconnect && (
        <div className="mono" style={{ fontSize: 9, color: "var(--error)", marginTop: 6 }}>
          ● {isIdentity
            ? (tr ? "Kimlik doğrulaması gerekli" : "Confirm identity")
            : (tr ? "Yeniden bağlanmalı" : "Reconnect needed")}
        </div>
      )}
      {!account && (
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleConnectClick}
          disabled={oauthMutation.isPending}
          style={{
            marginTop: 8,
            padding: "3px 8px",
            fontSize: 11,
            width: "100%",
            justifyContent: "center",
          }}
        >
          {oauthMutation.isPending
            ? (tr ? "Bağlanıyor…" : "Connecting…")
            : tr ? "Bağla" : "Connect"}
        </button>
      )}
      {needsReconnect && (
        <>
          {(isIdentity || identityGated) && (
            <div style={{ fontSize: 10, color: "var(--error)", marginTop: 6, lineHeight: 1.35 }}>
              {identityGateBody(tr)}
            </div>
          )}
          {identityGated && identityConfirm && (
            <IdentityGateConfirm
              tr={tr}
              compact
              pending={oauthMutation.isPending}
              onConfirm={() => oauthMutation.mutate()}
              onCancel={() => setIdentityConfirm(false)}
            />
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={handleConnectClick}
            disabled={oauthMutation.isPending}
            style={{
              marginTop: 8,
              padding: "3px 8px",
              fontSize: 11,
              width: "100%",
              justifyContent: "center",
            }}
          >
            {oauthMutation.isPending
              ? (tr ? "Bağlanıyor…" : "Connecting…")
              : tr ? "Yeniden bağlan" : "Reconnect"}
          </button>
        </>
      )}
      {errMsg ? (
        <div style={{ fontSize: 10, color: "var(--error)", marginTop: 6, lineHeight: 1.3 }}>
          {errMsg}
        </div>
      ) : null}
    </div>
  );
}

function SkeletonList() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="skel" style={{ height: 180, borderRadius: 12 }} />
      ))}
    </div>
  );
}

function EmptyState({ tr, onAdd }: { tr: boolean; onAdd: () => void }) {
  return (
    <div className="card" style={{ padding: "64px 24px", textAlign: "center" }}>
      <div className="eyebrow" style={{ marginBottom: 12, justifyContent: "center" }}>
        {tr ? "Boş workspace" : "Empty workspace"}
      </div>
      <div
        style={{
          fontFamily: "var(--display)",
          fontSize: 22,
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        {tr ? "Önce bir marka ekle" : "Add a brand first"}
      </div>
      <div
        style={{
          color: "var(--tx2)",
          fontSize: 13,
          maxWidth: 380,
          margin: "0 auto 16px",
        }}
      >
        {tr
          ? "Sosyal hesapları markaya bağlarsın — workspace seviyesinde değil."
          : "Social accounts attach to brands — not to the workspace."}
      </div>
      <button className="btn btn-primary" onClick={onAdd}>
        + {tr ? "Yeni marka" : "New brand"}
      </button>
    </div>
  );
}
